/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn, Stack } from 'component-library';
import { FC, forwardRef, Fragment, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import {
  Area,
  Bar,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  TooltipProps,
} from 'recharts-overriden';

import { UnitEnum } from '@/api/rest/resources/types/units';
import { ChartLegendBar } from '@/components/Charts';
import { Chart } from '@/components/Charts/components/Chart';
import { RadialGradient } from '@/components/Charts/components/RadialGradient';
import {
  ChartTooltipDot,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from '@/components/Charts/components/Tooltip';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { GraphFact } from '@/components/Charts/types';
import { getChartDataFromGraphFact, sortChartData } from '@/components/Charts/utils';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { isNullish } from '@/utils/booleans';
import { valueToTonne } from '@/utils/formatting';

export enum ChartShape {
  radar = 'radar',
  area = 'area',
  bar = 'bar',
  line = 'line',
  pie = 'pie',
}

export type PieChartProps = {
  innerRadius?: number;
  outerRadius?: number;
  startAngle?: number;
  endAngle?: number;
  paddingAngle?: number;
  cx?: string;
  cy?: string;
};

export type ChartElementConfig = {
  shape: ChartShape;
  dataKey: string;
  label: string;
  color: string;
  barSize?: number;
  key: string;
  // TODO: MVP-CW - update this map key and id better for existing graphs
  id: string;
  unit?: string;
  formatter?: (value: any) => any;
  className?: string;

  showLabel?: boolean;
  labelPosition?: 'top' | 'center' | 'bottom' | 'left' | 'right';
  [key: string]: any;
} & PieChartProps;

export type PieChartConfig = Omit<ChartElementConfig, 'shape'> & { shape: ChartShape.pie };

export type CompositeGraphProps = HTMLAttributes<HTMLDivElement> & {
  data: any;
  chartConfig: ChartElementConfig[];
  xAxisProps?: any;
  yAxisProps?: any;
  legendItems?: ChartLegendBarItem[];
  tooltip?: (props: TooltipProps<ValueType, NameType>) => JSX.Element;
};

const isGraphFact = (fact: GraphFact): fact is GraphFact => !!fact?.date;

export const CompositeGraph = forwardRef<HTMLDivElement, CompositeGraphProps>(
  // eslint-disable-next-line sonarjs/cognitive-complexity
  ({ data, chartConfig, xAxisProps = {}, yAxisProps = {}, legendItems = [], tooltip, ...delegated }, ref) => {
    let chartData: any[] = data;
    let computedXAxisProps = xAxisProps;
    const isGraphFactArray = chartData.length > 0 && isGraphFact(chartData[0]);
    if (isGraphFactArray) {
      const factNamesDict = chartConfig.reduce(
        (acc, cur) => {
          acc[`${cur.key}`] = cur.id;
          return acc;
        },
        {} as Record<string, string>,
      );
      const formatter = chartConfig.find((cfg) => typeof cfg.formatter === 'function')?.formatter;
      const chartDataMap = getChartDataFromGraphFact(data, factNamesDict, formatter);
      chartData = sortChartData(chartDataMap, (date) => new Date(date).getTime());

      // Set up x-axis configuration to only show years with actual data
      // This prevents gaps and ensures uniform tick spacing for available data points
      if (chartData.length > 0) {
        // For GraphFact data, we want to show ticks only for years that have data
        // The chart will automatically space the ticks evenly based on the data points
        computedXAxisProps = {
          dataKey: 'label',
          type: 'category',
          ...xAxisProps,
        };
      }
    }
    const hasPolarCharts = chartConfig.some((cfg) => cfg.shape === ChartShape.radar);
    const hasPieCharts = chartConfig.some((cfg) => cfg.shape === ChartShape.pie);

    if (hasPolarCharts) {
      return (
        <>
          <Chart.Container
            {...delegated}
            data={chartData}
            config={chartConfig.reduce(
              (acc, cur) => {
                acc[cur.dataKey] = { label: cur.label, color: cur.color };
                return acc;
              },
              {} as Record<string, { label: string; color: string }>,
            )}
          >
            <RadarChart data={chartData}>
              {chartConfig
                .filter((cfg) => cfg.shape === ChartShape.radar)
                .map((cfg) => {
                  const commonProps = {
                    activeDot: cfg.activeDot !== undefined ? cfg.activeDot : ChartTooltipDot,
                  };
                  return (
                    <Fragment key={`radar-group-${cfg.dataKey}`}>
                      <defs key={`defs-${cfg.dataKey}`}>
                        {cfg.gradient && (
                          <RadialGradient
                            id={cfg.gradient.id}
                            cx={cfg.gradient.cx}
                            cy={cfg.gradient.cy}
                            r={cfg.gradient.r}
                            stops={cfg.gradient.stops}
                          />
                        )}
                      </defs>
                      <PolarGrid key={`radar-grid-${cfg.dataKey}`} />
                      <Radar
                        stroke={cfg.color}
                        fill={cfg.fill}
                        {...{ ...cfg, shape: undefined, gradient: undefined }}
                        {...commonProps}
                        key={`radar-${cfg.dataKey}`}
                      />
                      <PolarAngleAxis key={`radar-axis-${cfg.dataKey}`} {...computedXAxisProps} />
                    </Fragment>
                  );
                })}
              <Chart.Tooltip
                content={
                  tooltip || ((props) => <CustomTooltip {...props} xAxisProps={xAxisProps} chartConfig={chartConfig} />)
                }
              />
            </RadarChart>
          </Chart.Container>
          {legendItems.length > 0 && <ChartLegendBar items={legendItems} className='sm:ml-[28px]' />}
        </>
      );
    }

    if (hasPieCharts) {
      return (
        <PieChartGraph
          {...delegated}
          chartData={chartData}
          chartConfig={chartConfig}
          legendItems={legendItems}
          tooltip={tooltip}
          xAxisProps={xAxisProps}
        />
      );
    }

    return (
      <>
        <Chart.Container
          {...delegated}
          ref={ref}
          data={chartData}
          config={chartConfig.reduce(
            (acc, cur) => {
              acc[cur.dataKey] = { label: cur.label, color: cur.color };
              return acc;
            },
            {} as Record<string, { label: string; color: string }>,
          )}
        >
          <ComposedChart data={chartData}>
            {chartConfig.map((cfg) => {
              const commonProps = {
                activeDot: cfg.activeDot !== undefined ? cfg.activeDot : ChartTooltipDot,
              };
              if (cfg.shape === ChartShape.bar) {
                return (
                  <Bar
                    fill={cfg.fill}
                    barSize={cfg.barSize || 20}
                    {...{ ...cfg, shape: undefined }}
                    {...commonProps}
                    key={cfg.dataKey}
                  >
                    {chartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={cfg.barColors[index % cfg.barColors.length]} />
                    ))}
                    {cfg.showLabel && (
                      <LabelList
                        dataKey={cfg.dataKey}
                        position='center'
                        content={({ value, x, y, width, height, payload }: any) => {
                          const individualValue = payload?.[cfg?.dataKey] ?? value;

                          if (!individualValue || individualValue === 0) return null;

                          const displayValue = cfg.formatter ? cfg.formatter(individualValue) : individualValue;
                          const unit = cfg.unit || '';
                          const labelText = `${displayValue}${unit}`;

                          const centerX = (x as number) + (width as number) / 2;
                          const centerY = (y as number) + (height as number) / 2;

                          return (
                            <text
                              x={centerX}
                              y={centerY}
                              textAnchor='middle'
                              dominantBaseline='middle'
                              fill='black'
                              fontSize='12'
                              fontWeight='500'
                            >
                              {labelText}
                            </text>
                          );
                        }}
                      />
                    )}
                  </Bar>
                );
              }
              if (cfg.shape === ChartShape.line) {
                return (
                  <Line
                    connectNulls
                    isAnimationActive={false}
                    stroke={cfg.color}
                    dot={{ stroke: cfg.color }}
                    {...cfg}
                    {...commonProps}
                    key={cfg.dataKey}
                  />
                );
              }
              if (cfg.shape === ChartShape.area) {
                return <Area stroke={cfg.color} fill={cfg.color} {...cfg} {...commonProps} key={cfg.dataKey} />;
              }
              return null;
            })}
            <Chart.XAxis {...{ ...(Chart.XAxis.defaultProps || {}), ...{ ...computedXAxisProps, label: '' } }}>
              {computedXAxisProps?.label && <Chart.Label>{computedXAxisProps.label}</Chart.Label>}
            </Chart.XAxis>

            <Chart.YAxis {...{ ...(Chart.YAxis.defaultProps || {}), ...{ ...yAxisProps, label: '' } }}>
              {yAxisProps?.label && <Chart.Label>{yAxisProps.label}</Chart.Label>}
            </Chart.YAxis>
            <Chart.Tooltip
              allowEscapeViewBox={{ x: false, y: true }}
              content={
                tooltip ||
                ((props) => <CustomTooltip {...props} xAxisProps={computedXAxisProps} chartConfig={chartConfig} />)
              }
            />
          </ComposedChart>
        </Chart.Container>
        {legendItems.length > 0 && <ChartLegendBar items={legendItems} className='sm:ml-[28px] my-4' />}
      </>
    );
  },
);

CompositeGraph.displayName = 'CompositeGraph';

type PieChartsGraphProps = HTMLAttributes<HTMLDivElement> & {
  chartData: any[];
  chartConfig: ChartElementConfig[];
  legendItems: ChartLegendBarItem[];
  tooltip?: (props: TooltipProps<ValueType, NameType>) => JSX.Element;
  xAxisProps?: any;
};

const PieChartGraph: FC<PieChartsGraphProps> = ({
  chartData,
  chartConfig,
  legendItems,
  tooltip,
  xAxisProps,
  ...delegated
}) => {
  const pieConfig = chartConfig.find((cfg) => cfg.shape === ChartShape.pie) as PieChartConfig;

  const chartProps = {
    cx: pieConfig?.cx || '50%',
    cy: pieConfig?.cy || '50%',
    innerRadius: pieConfig?.innerRadius || 0,
    outerRadius: pieConfig?.outerRadius || 80,
    paddingAngle: pieConfig?.paddingAngle || 0,
    dataKey: pieConfig?.dataKey || 'value',
    startAngle: pieConfig?.startAngle || 0,
    endAngle: pieConfig?.endAngle || 360,
  };

  return (
    <>
      <Chart.Container
        {...delegated}
        data={chartData}
        config={chartConfig.reduce(
          (acc, cur) => {
            acc[cur.dataKey] = { label: cur.label, color: cur.color };
            return acc;
          },
          {} as Record<string, { label: string; color: string }>,
        )}
        height={pieConfig?.height || 300}
      >
        <PieChart>
          <Pie data={chartData} {...chartProps}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} className='outline-none' />
            ))}
          </Pie>
          <Chart.Tooltip
            content={
              tooltip || ((props) => <CustomTooltip {...props} xAxisProps={xAxisProps} chartConfig={chartConfig} />)
            }
          />
        </PieChart>
      </Chart.Container>
      <ChartLegendBar items={legendItems} className='my-4 w-full items-center justify-center' />
    </>
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  xAxisProps,
  chartConfig,
}: TooltipProps<ValueType, NameType> & { xAxisProps?: any; chartConfig?: ChartElementConfig[] }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;

  const tickFormatter = xAxisProps?.tickFormatter;
  const formattedLabel = tickFormatter ? tickFormatter(label) : label;

  const configMap = Array.isArray(chartConfig)
    ? chartConfig.reduce(
        (acc, cur) => {
          acc[cur.dataKey] = { label: cur.label || cur.dataKey, unit: cur.unit || '', className: cur.className || '' };
          return acc;
        },
        {} as Record<string, { label: string; unit: string; className: string }>,
      )
    : {};

  return (
    <Stack direction='col' spacing={1.5} className='rounded-md bg-white-100 px-2 py-1 elevation-1'>
      <Stack spacing={2}>
        <ChartTooltipTextSecondary>{formattedLabel}</ChartTooltipTextSecondary>
        {payload
          .sort((a, b) => ((a.value ?? 0) > (b.value ?? 0) ? -1 : 1))
          .map((item) => {
            const config = configMap[item.dataKey as string];
            let itemValue = item.value;
            const displayValue = !isNullish(itemValue)
              ? `${getDisplayNumber(itemValue as string, window.navigator.language)} ${config?.unit}`
              : t('global.analysis.noData');

            if ((item.dataKey as string).includes('health')) {
              const itemId = (item.dataKey as string)?.replace('.value', '') as string;
              itemValue = item.value;
              const itemValueDisplay = `${getDisplayNumber(itemValue as string, window.navigator.language)} ${'%'}`;

              let itemRawValue = item.payload[`${itemId}`]?.metadata?.raw_value ?? '';
              itemRawValue =
                itemId.includes('carbon') || itemId.includes('soil')
                  ? valueToTonne(itemRawValue, UnitEnum['kg/ha'])
                  : itemRawValue;
              const itemRawValueDisplay = `${getDisplayNumber(itemRawValue as string, window.navigator.language)} ${config?.unit}`;

              return (
                <Stack
                  spacing={8}
                  direction='row'
                  key={item.dataKey}
                  className={cn('justify-between', config?.className)}
                >
                  <ChartTooltipTextPrimary>{config?.label}</ChartTooltipTextPrimary>
                  <Stack direction='row' spacing={2}>
                    <ChartTooltipTextSecondary>{itemRawValueDisplay}</ChartTooltipTextSecondary>
                    <ChartTooltipTextPrimary>{itemValueDisplay}</ChartTooltipTextPrimary>
                  </Stack>
                </Stack>
              );
            }

            return (
              <Stack
                spacing={8}
                direction='row'
                key={item.dataKey}
                className={cn('justify-between', config?.className)}
              >
                <ChartTooltipTextPrimary>{config?.label}</ChartTooltipTextPrimary>
                <ChartTooltipTextPrimary>{displayValue}</ChartTooltipTextPrimary>
              </Stack>
            );
          })}
      </Stack>
    </Stack>
  );
};
